import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, TextField, themeVars, Button, AlertInline } from '@eds-web/react';

import { useGuardianForm } from '../../hooks/useGuardianForm';
import { GuardianInfo } from '../../types/legal.types';

interface GuardianFormProps {
    initialGuardianInfo?: GuardianInfo;
}

export const GuardianForm: React.FC<GuardianFormProps> = () => {
    const { t } = useTranslation();
    const { formik, handleChangeFormValues, updating, loading, duplicatedEmail } =
        useGuardianForm();

    const duplicatedEmailMessage = t(
        'errors.com.epicgames.ecommerce.payout.payoutaccount.duplicated'
    );

    return (
        <div>
            <Text style={{ marginBottom: 24 }} variant="headingMD">
                {t('epic.esport.guardian.title')}
            </Text>

            <form onSubmit={formik.handleSubmit} noValidate>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '16px',
                        marginBottom: '16px'
                    }}
                >
                    <TextField
                        label={t('epic.esport.label.profile.guardian.firstName')}
                        name="guardianFirstName"
                        value={formik.values.guardianFirstName}
                        onChange={handleChangeFormValues}
                        placeholder={t('epic.esport.label.profile.guardian.firstName')}
                        disabled={updating || loading}
                        error={formik.touched.guardianFirstName && formik.errors.guardianFirstName}
                        required
                    />

                    <TextField
                        label={t('epic.esport.label.profile.guardian.lastName')}
                        name="guardianLastName"
                        value={formik.values.guardianLastName}
                        onChange={handleChangeFormValues}
                        placeholder={t('epic.esport.label.profile.guardian.lastName')}
                        disabled={updating || loading}
                        error={formik.touched.guardianLastName && formik.errors.guardianLastName}
                        required
                    />
                </div>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '16px',
                        marginBottom: '16px'
                    }}
                >
                    <TextField
                        label={t('epic.esport.label.profile.guardian.email')}
                        name="guardianEmail"
                        type="email"
                        value={formik.values.guardianEmail}
                        onChange={handleChangeFormValues}
                        placeholder={t('epic.esport.label.profile.guardian.email')}
                        disabled={updating || loading}
                        error={formik.touched.guardianEmail && formik.errors.guardianEmail}
                        required
                    />
                    <TextField
                        label={t('epic.esport.label.profile.guardian.phone')}
                        name="guardianPhoneNumber"
                        value={formik.values.guardianPhoneNumber}
                        onChange={handleChangeFormValues}
                        placeholder={t('epic.esport.label.profile.guardian.phone')}
                        disabled={updating || loading}
                        error={
                            formik.touched.guardianPhoneNumber && formik.errors.guardianPhoneNumber
                        }
                        required
                    />
                </div>

                {duplicatedEmail && (
                    <AlertInline
                        style={{ marginBottom: 24 }}
                        status={'critical'}
                        message={duplicatedEmailMessage}
                    />
                )}

                <Text
                    variant="paragraphSM"
                    style={{ color: themeVars.color.transparent.light['065'] }}
                >
                    {t('epic.esport.guardian.notice')}
                </Text>
                <Button
                    style={{ marginTop: 24 }}
                    type="submit"
                    disabled={updating || !formik.dirty || !formik.isValid || loading}
                    loading={updating}
                    loadingLabel={t('epic.common.loading')}
                >
                    {t('epic.esport.profile.saveChanges')}
                </Button>
            </form>
        </div>
    );
};
